import { FormatOptions, formatter } from '@/components/AnalyticsDashboard/formatter'
import { Axis, createAxis } from './axes'
import { Ticks, createTicks, responsiveLabels } from './ticks'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { Chart } from './chart'
import { DataSource } from '@/composables/useDataSources'
import { ChartComponent } from '..'
import { createTooltip } from './tooltip'
import { Limits, calculateLimitsFromValues, combineLimits } from './limits'
import { Pattern, createBars } from './bars'
import { AreaHoverHighlight, createAreaHightlights } from './area-highlight'
import { createErrorText } from './utils'
import { transpose } from 'd3'
import { bucketSizeToFormat } from '../utils'
import { BucketSize } from '@/api-client/models/indoorclimate/Contracts'
import { MeasurementPoint } from '@/components/AnalyticsDashboard/measurements/contracts'
import { ColorScaleConfig, getColorForValue } from '@/utils/colorScale'

// The default format for the codomain (y-axis for vertical bars, x-axis for horizontal bars)
const DEFAULT_CODOMAIN_FORMAT: FormatOptions = {
  format: {
    type: 'intl-number',
    style: 'decimal',
    maximumFractionDigits: 2
  }
}

const DEFAULT_HORIZONTAL_AXIS: Axis<'top' | 'bottom'> = {
  position: 'bottom',
  showTicks: false,
  showLine: false
}

const DEFAULT_VERTICAL_AXIS: Axis<'left' | 'right'> = {
  position: 'left',
  showTicks: false,
  showLine: false,
  lines: true
}

type BarFormat = {
  color: string
  label: LocaleString
  pattern?: Pattern
  gradient?: boolean
}

export type BarChart = {
  verticalAxis: Partial<Axis<'left' | 'right'>>
  horizontalAxis?: Partial<Axis<'top' | 'bottom'>>
  codomainFormat?: FormatOptions
  codomainTicks?: Ticks
  domainFormat?: FormatOptions
  domainTicks?: Ticks
  dataSources?: string[]
  valueFilterTreshold?: number
  barFormats: BarFormat[]
  legendColor: string
  missingDataMessage: LocaleString
  bucketSize?: BucketSize
}

export type BarValue = {
  label: string
  height: number
}

const getData = (
  chart: Chart<'bar'>,
  dataSources: Record<string, DataSource<BarValue[]> | undefined>
): BarValue[][] => {
  if (!chart.dataSources) return []
  return chart.dataSources.map((datasource) => dataSources[datasource]?.data ?? [])
}

const valuesToPoints = (xValues: number[], yValues: number[][]): [number, number][][] =>
  transpose(transpose(yValues).map((y) => xValues.map((x) => [x, y[x]])))

export function createBarChart(
  chart: Chart<'bar'>,
  dataSources: Record<string, DataSource<BarValue[]> | undefined>,
  viewport: { width: number; height: number },
  locale: string
): ChartComponent[] | undefined {
  const data = getData(chart, dataSources)

  const width = 0.5
  const xValues = data[0].map((_, i) => i)
  const yValues = transpose(data.map((values) => values.map((value) => value.height)))

  const dataFormat = chart.codomainFormat ?? DEFAULT_CODOMAIN_FORMAT
  const horizontalAxis: Axis<'top' | 'bottom'> = {
    ...DEFAULT_HORIZONTAL_AXIS,
    ...chart.horizontalAxis
  }
  const verticalAxis: Axis<'left' | 'right'> = {
    ...DEFAULT_VERTICAL_AXIS,
    ...chart.verticalAxis
  }
  const ySums = yValues.map((y) => y.reduce((a, b) => a + b, 0))
  const xData = xValues
    .flat()
    .concat([xValues[0] - width / 2, xValues[xValues.length - 1] + width / 2])
  const dataLimits: Limits = {
    x: [Math.min(...xData), Math.max(...xData)],
    y: [0, Math.max(...ySums)]
  }
  const timeFormat = chart.bucketSize ? bucketSizeToFormat[chart.bucketSize] : undefined
  const horizontalTicks: Ticks<'complete'> = responsiveLabels(
    {
      values: xValues,
      labels: data[0].map((v) => (timeFormat ? formatter(v.label, locale, timeFormat) : v.label))
    },
    viewport.width
  )
  const verticalTicks = createTicks(
    chart.codomainTicks ?? 'auto',
    dataLimits.y,
    [],
    dataFormat,
    locale
  )
  const tickLimits = calculateLimitsFromValues(horizontalTicks.values, verticalTicks.values)
  const limits = combineLimits([tickLimits, dataLimits])
  const points = valuesToPoints(xValues, yValues)
  if (points.length === 0) return createErrorText(chart.missingDataMessage, locale)
  if (points[0].length === 0 && points[1].length === 0)
    return createErrorText(chart.missingDataMessage, locale)
  const bars = transpose(data).map((bars, i) =>
    bars.map((bar, j) => ({
      ...chart.barFormats[j],
      label: chart.barFormats[j].label[locale],
      x: i,
      height: bar.height,
      gradient: true
    }))
  )
  const barComponents = createBars(bars, width, 'vertical', limits)

  const highlights: AreaHoverHighlight[] = bars
    .map((bars, i) =>
      bars.map((bar, j): AreaHoverHighlight => {
        const k = i * bars.length + j
        return {
          label: bar.label,
          color: bar.color ?? 'red',
          pattern: bar.pattern,
          path: barComponents[k].values as [number, number][],
          value: [bar.x, bar.height],
          visible: false
        }
      })
    )
    .flat()

  return [
    ...createAxis(horizontalAxis, horizontalTicks, limits.x),
    ...createAxis(verticalAxis, verticalTicks, limits.y),
    ...barComponents,
    ...createTooltip(limits, dataFormat),
    ...createAreaHightlights(highlights, limits)
  ]
}

export type BarChartConfig = {
  verticalAxis: Partial<Axis<'left' | 'right'>>
  horizontalAxis?: Partial<Axis<'top' | 'bottom'>>
  codomainFormat?: FormatOptions
  codomainTicks?: Ticks
  domainFormat?: FormatOptions
  domainTicks?: Ticks
  dataSources?: string[]
  valueFilterTreshold?: number
  barFormats: Record<string, BarFormat>
  subBarFormats?: Record<string, BarFormat>
  colorScale?: ColorScaleConfig
  legendColor: string
  missingDataMessage: LocaleString
  xKey?: string
  yKey?: string
}

function anySortOrder(a: any, b: any): number {
  switch (typeof a) {
    case 'number':
      return (a as number) - (b as number)
    case 'string': {
      // check if date
      const dateA = new Date(a)
      const dateB = new Date(b)
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        return dateA.getTime() - dateB.getTime()
      }
      return a.localeCompare(b)
    }
    case 'boolean':
      return (a as boolean) === (b as boolean) ? 0 : a ? 1 : -1
    default:
      return 0
  }
}

export function createBarChartNew(
  chart: BarChartConfig,
  measurements: MeasurementPoint[],
  viewport: { width: number; height: number },
  locale: string
): ChartComponent[] | undefined {
  if (!measurements.length) return createErrorText(chart.missingDataMessage, locale)

  const xKey = chart.xKey
  const yKey = chart.yKey

  const toMaybeDate = (
    value: string | number | null | undefined
  ): Date | string | number | undefined => {
    if (value === null || value === undefined) return undefined
    if (typeof value === 'number') return value
    const date = new Date(value)
    if (isNaN(date.getTime())) return value
    return date
  }

  interface BarSegmentData {
    x: Date | string | number | undefined
    y: string | undefined
    field: string
    height: number
  }

  const allSegments: BarSegmentData[] = measurements.flatMap((measurement) => {
    const rawXGroupValue = xKey ? measurement.groups[xKey] : undefined
    const rawYGroupValue = yKey ? measurement.groups[yKey] : undefined

    const yGroupString =
      rawYGroupValue === null || rawYGroupValue === undefined ? undefined : String(rawYGroupValue)

    return Object.entries(measurement.fields).map(([fieldStr, value]) => {
      const xValue = xKey ? toMaybeDate(rawXGroupValue) : fieldStr

      return {
        x: xValue,
        y: yGroupString,
        field: fieldStr,
        height: value as number
      }
    })
  })

  if (allSegments.length === 0) {
    return createErrorText(chart.missingDataMessage, locale)
  }

  const segmentsByX = new Map<string | number, BarSegmentData[]>()
  allSegments.forEach((segment) => {
    let keyForMap: string | number
    if (segment.x instanceof Date) {
      keyForMap = segment.x.getTime()
    } else if (typeof segment.x === 'number') {
      keyForMap = segment.x
    } else {
      keyForMap = String(segment.x)
    }

    if (!segmentsByX.has(keyForMap)) {
      segmentsByX.set(keyForMap, [])
    }
    segmentsByX.get(keyForMap)!.push(segment)
  })

  const sortedXMapKeys = Array.from(segmentsByX.keys()).sort((aKey, bKey) => {
    const aX = segmentsByX.get(aKey)![0].x
    const bX = segmentsByX.get(bKey)![0].x
    return anySortOrder(aX, bX)
  })

  const data: BarSegmentData[][] = sortedXMapKeys.map((mapKey) => {
    const segmentsForThisX = segmentsByX.get(mapKey)!

    segmentsForThisX.sort((a, b) => {
      if (yKey && a.y !== b.y) {
        return anySortOrder(a.y, b.y)
      }
      return anySortOrder(a.field, b.field)
    })
    return segmentsForThisX
  })

  if (data.length === 0 || data.every((column) => column.length === 0)) {
    return createErrorText(chart.missingDataMessage, locale)
  }

  const getX = (
    segmentData: { x: Date | string | number | undefined },
    xCategoryIndex: number
  ): number => {
    if (segmentData.x === null || segmentData.x === undefined) return xCategoryIndex
    switch (typeof segmentData.x) {
      case 'number':
        return segmentData.x
      case 'object':
        if (segmentData.x instanceof Date) return segmentData.x.getTime()
        return xCategoryIndex
      case 'string':
        return xCategoryIndex
      default:
        return xCategoryIndex
    }
  }

  const xValues = data.map((segmentsInXCategory, i) => getX(segmentsInXCategory[0], i))

  const barWidthRatio = 0.8
  let widthOfXCategory: number
  if (xValues.length > 1) {
    const differences: number[] = []
    for (let i = 0; i < xValues.length - 1; i++) {
      differences.push(Math.abs(xValues[i + 1] - xValues[i]))
    }
    const minDiff = Math.min(...differences)
    widthOfXCategory = (minDiff > 0 ? minDiff : 1.0) * barWidthRatio
  } else if (xValues.length === 1) {
    widthOfXCategory = 1.0 * barWidthRatio
  } else {
    widthOfXCategory = 0
  }
  if (widthOfXCategory <= 0 && xValues.length > 0) {
    widthOfXCategory = 1.0 * barWidthRatio
  }

  const yValuesPerXCategory = data.map((segmentsAtX) =>
    segmentsAtX.map((segment) => segment.height)
  )

  const dataFormat = chart.codomainFormat ?? DEFAULT_CODOMAIN_FORMAT
  const horizontalAxis: Axis<'top' | 'bottom'> = {
    ...DEFAULT_HORIZONTAL_AXIS,
    ...chart.horizontalAxis
  }
  const verticalAxis: Axis<'left' | 'right'> = {
    ...DEFAULT_VERTICAL_AXIS,
    ...chart.verticalAxis
  }

  const ySums = yValuesPerXCategory.map((yStack) => yStack.reduce((a, b) => a + b, 0))

  const xDomainForLimits: number[] = []
  if (xValues.length > 0) {
    xDomainForLimits.push(...xValues)
    xDomainForLimits.push(xValues[0] - widthOfXCategory / 2)
    xDomainForLimits.push(xValues[xValues.length - 1] + widthOfXCategory / 2)
  } else {
    xDomainForLimits.push(0 - widthOfXCategory / 2)
    xDomainForLimits.push(0 + widthOfXCategory / 2)
  }

  const dataLimits: Limits = {
    x:
      xDomainForLimits.length > 0
        ? [Math.min(...xDomainForLimits), Math.max(...xDomainForLimits)]
        : [0, 1],
    y: [0, Math.max(0, ...ySums, 1)]
  }

  const horizontalTicks: Ticks<'complete'> = responsiveLabels(
    {
      values: xValues,
      labels: data.map((segments, i_xCat) => {
        const firstSegmentForXCat = segments[0]
        const originalXValue = firstSegmentForXCat.x
        if (originalXValue instanceof Date || typeof originalXValue === 'number') {
          return formatter(
            originalXValue instanceof Date ? originalXValue.getTime() : originalXValue,
            locale,
            chart.domainFormat ?? bucketSizeToFormat['DAY']
          )
        } else {
          const labelKey =
            originalXValue === null || originalXValue === undefined
              ? String(originalXValue)
              : originalXValue
          return (
            (chart.barFormats && chart.barFormats[labelKey as string]?.label[locale]) ??
            labelKey?.toString() ??
            i_xCat.toString()
          )
        }
      })
    },
    viewport.width
  )

  const verticalTicks = createTicks(
    chart.codomainTicks ?? 'auto',
    dataLimits.y,
    [],
    dataFormat,
    locale
  )
  const tickLimits = calculateLimitsFromValues(horizontalTicks.values, verticalTicks.values)
  const limits = combineLimits([tickLimits, dataLimits])

  const colorScale = chart.colorScale

  const barsDisplayObjects = data.map((segmentsAtX, i_xCat) =>
    segmentsAtX.map((segment) => {
      const barFormatForField = chart.barFormats?.[segment.field]
      const subBarFormatForY = segment.y ? chart.subBarFormats?.[segment.y] : undefined

      return {
        m: segment,
        ...(barFormatForField || {}),
        color:
          subBarFormatForY?.color ??
          (colorScale ? getColorForValue(colorScale, segment.height).toHex() : undefined) ??
          barFormatForField?.color ??
          '#cccccc',
        label:
          subBarFormatForY?.label?.[locale] ??
          barFormatForField?.label?.[locale] ??
          segment.field ??
          '?',
        gradient: true,
        x: getX(segment, i_xCat),
        height: segment.height,
        pattern: subBarFormatForY?.pattern ?? barFormatForField?.pattern
      }
    })
  )

  const barComponents = createBars(barsDisplayObjects, widthOfXCategory, 'vertical', limits)

  const highlights: AreaHoverHighlight[] = barsDisplayObjects
    .flat()
    .map((barDisplayObj, k): AreaHoverHighlight => {
      const correspondingBarComponent = barComponents[k]
      if (!correspondingBarComponent) {
        return {
          label: 'Error',
          color: 'transparent',
          path: [],
          value: [0, 0],
          visible: false
        }
      }
      return {
        label: barDisplayObj.label,
        color: barDisplayObj.color ?? 'red',
        pattern: barDisplayObj.pattern,
        path: correspondingBarComponent.values as [number, number][],
        value: [barDisplayObj.x, barDisplayObj.height],
        visible: false
      }
    })

  return [
    ...createAxis(horizontalAxis, horizontalTicks, limits.x),
    ...createAxis(verticalAxis, verticalTicks, limits.y),
    ...barComponents,
    ...createTooltip(limits, dataFormat),
    ...createAreaHightlights(highlights, limits)
  ]
}
